import { featureToggleService } from '@/common/api/services/featureToggles';
import { queryClient } from '@/index';
import { notifyFeatureTogglesUpdated } from '@/common/hooks/useFeatureFlag';

declare global {
    interface Window {
        featureToggles?: Record<string, boolean>;
    }
}

const FEATURE_TOGGLES_KEY = ['feature-toggles-frontend'];

export class FeatureToggleService {
    private static instance: FeatureToggleService;
    private lastFetchTime: number = 0;
    private fetchInProgress: boolean = false;
    private fetchInterval: number = 5 * 60 * 1000;

    public static getInstance(): FeatureToggleService {
        if (!FeatureToggleService.instance) {
            FeatureToggleService.instance = new FeatureToggleService();
        }
        return FeatureToggleService.instance;
    }

    private constructor() {
        if (typeof window !== 'undefined' && !window.featureToggles) {
            window.featureToggles = {};
        }
    }

    public async initialize(): Promise<void> {
        await this.fetchFeatureToggles();

        setInterval(() => this.fetchFeatureToggles(), this.fetchInterval);
    }

    public async fetchFeatureToggles(): Promise<Record<string, boolean>> {
        const now = Date.now();

        if (this.fetchInProgress || (now - this.lastFetchTime < 60000 && this.lastFetchTime !== 0)) {
            const cachedData = queryClient.getQueryData<Record<string, boolean>>(FEATURE_TOGGLES_KEY);

            if (cachedData) {
                return cachedData;
            }

            const windowToggles = window.featureToggles || {};
            return windowToggles;
        }

        try {
            this.fetchInProgress = true;

            const toggles = await featureToggleService.getFeatureTogglesForFrontend();

            queryClient.setQueryData(FEATURE_TOGGLES_KEY, toggles);

            if (typeof window !== 'undefined') {
                window.featureToggles = toggles;

                notifyFeatureTogglesUpdated();
            }

            this.lastFetchTime = Date.now();
            return toggles;
        } catch (error) {
            const windowToggles = window.featureToggles || {};
            return windowToggles;
        } finally {
            this.fetchInProgress = false;
        }
    }

    public isEnabled(key: string): boolean {
        if (typeof window === 'undefined') {
            return false;
        }

        return Boolean(window.featureToggles?.[key]);
    }

    public async refreshToggles(): Promise<void> {
        this.lastFetchTime = 0;
        await this.fetchFeatureToggles();

        notifyFeatureTogglesUpdated();
    }

    public setLocalToggle(key: string, value: boolean): void {
        if (typeof window === 'undefined') {
            return;
        }

        window.featureToggles = {
            ...window.featureToggles,
            [key]: value
        };

        const cachedData = queryClient.getQueryData<Record<string, boolean>>(FEATURE_TOGGLES_KEY);
        if (cachedData) {
            queryClient.setQueryData(FEATURE_TOGGLES_KEY, {
                ...cachedData,
                [key]: value
            });
        }

        notifyFeatureTogglesUpdated();
    }
}

export const featureToggleServiceInstance = FeatureToggleService.getInstance(); 